import { z } from 'zod'
import type { FormFieldType, FieldTypeType } from '@/lib/types/contact'

// ── Types ────────────────────────────────────────────────────────────────────

export type ContactFormField = FormFieldType & {
  fieldName: string
  fieldType: FieldTypeType
}

// ── Validation constants ─────────────────────────────────────────────────────

/** Integer or decimal number; rejects letters and scientific notation. */
export const NUMBER_INPUT_RE = /^-?\d+(\.\d+)?$/

/** Digits only after stripping formatting; E.164-ish length (10–15). */
export const PHONE_DIGIT_COUNT_RE = /^\d{10,15}$/

/** Allows empty string or a valid number (for optional number fields). */
const OPTIONAL_NUMBER_RE = /^$|^-?\d+(\.\d+)?$/

// ── Field type helpers ───────────────────────────────────────────────────────

export function isEmailField(field: ContactFormField): boolean {
  if (field.fieldType === 'Email') return true
  return /\be-?mail\b/i.test(field.fieldName)
}

export function isPhoneField(field: ContactFormField): boolean {
  if (field.fieldType === 'Phone Number') return true
  return /\b(phone|mobile|tel|telephone)\b/i.test(field.fieldName)
}

export function isNameField(field: ContactFormField): boolean {
  if (isEmailField(field) || isPhoneField(field)) return false
  if (field.fieldType !== 'Text') return false
  return /\b(full\s*name|first\s*name|last\s*name|name)\b/i.test(field.fieldName)
}

export function isNumberField(field: ContactFormField): boolean {
  return field.fieldType === 'Number'
}

export function hasCheckboxOptions(field: ContactFormField): boolean {
  return (
    field.fieldType === 'Check Box' &&
    (field.options ?? []).some((o) => Boolean(o.point?.trim()))
  )
}

/** Single boolean/checkbox (no option list). Multi-option checkboxes are strings. */
export function isBooleanLikeField(field: ContactFormField): boolean {
  if (hasCheckboxOptions(field)) return false
  return field.fieldType === 'Boolean' || field.fieldType === 'Check Box'
}

/**
 * Stable RHF/Zod key. CMS `fieldName` can include apostrophes/spaces (e.g. "I'm a")
 * which break react-hook-form error path mapping.
 */
export function fieldKey(field: Pick<ContactFormField, 'id'>): string {
  return `field_${field.id}`
}

// ── Validators ───────────────────────────────────────────────────────────────

export function isValidEmail(value: string): boolean {
  return z.email().safeParse(value.trim()).success
}

export function isValidPhone(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed || /[^\d\s()+.-]/.test(trimmed)) return false
  const digits = trimmed.replace(/\D/g, '')
  return PHONE_DIGIT_COUNT_RE.test(digits)
}

/** Rejects number-only values; requires at least one letter. */
export function isValidName(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false
  const withoutSpaces = trimmed.replace(/\s+/g, '')
  if (/^\d+$/.test(withoutSpaces)) return false
  return /[A-Za-z\u00C0-\u024F]/.test(trimmed)
}

export function isValidNumberInput(value: string): boolean {
  return NUMBER_INPUT_RE.test(value.trim())
}

/** Strip characters that are not valid in a number field (keeps digits, one leading -, one .). */
export function sanitizeNumberInput(raw: string): string {
  let next = raw.replace(/[^\d.-]/g, '')
  next = next.replace(/(?!^)-/g, '')
  const dot = next.indexOf('.')
  if (dot !== -1) {
    next = next.slice(0, dot + 1) + next.slice(dot + 1).replace(/\./g, '')
  }
  return next
}

export function sanitizePhoneInput(raw: string): string {
  return raw.replace(/[^\d\s()+.-]/g, '')
}

export function validateContactFieldValue(
  field: ContactFormField,
  value: unknown,
): true | string {
  const label = field.fieldName
  const required = field.isRequired === true

  if (isBooleanLikeField(field)) {
    if (required && value !== true && value !== 'true') {
      return `${label} must be accepted`
    }
    return true
  }

  const str = typeof value === 'string' ? value.trim() : ''
  const isEmpty = str.length === 0

  if (required && isEmpty) {
    return `${label} is required`
  }

  if (isEmpty) return true

  if (isEmailField(field) && !isValidEmail(str)) {
    return `${label} must be a valid email address`
  }

  if (isPhoneField(field) && !isValidPhone(str)) {
    return `${label} must be a valid phone number`
  }

  if (isNameField(field) && !isValidName(str)) {
    return `${label} cannot be numbers only`
  }

  if (isNumberField(field) && !isValidNumberInput(str)) {
    return `${label} must be a valid number`
  }

  return true
}

// ── Schema + defaults ────────────────────────────────────────────────────────

export function buildContactFormSchema(fields: ContactFormField[]) {
  const shape: Record<string, z.ZodTypeAny> = {}

  for (const field of fields) {
    const key = fieldKey(field)
    const label = field.fieldName
    const required = field.isRequired === true
    const requiredMsg = `${label} is required`
    const emailMsg = `${label} must be a valid email address`
    const phoneMsg = `${label} must be a valid phone number`
    const nameMsg = `${label} cannot be numbers only`
    const numberMsg = `${label} must be a valid number`

    if (isBooleanLikeField(field)) {
      shape[key] = required
        ? z.boolean().refine((v) => v === true, { message: `${label} must be accepted` })
        : z.boolean()
      continue
    }

    if (isEmailField(field)) {
      shape[key] = required
        ? z.string().trim().min(1, requiredMsg).refine(isValidEmail, emailMsg)
        : z
            .string()
            .trim()
            .refine((v) => v === '' || isValidEmail(v), emailMsg)
      continue
    }

    if (isPhoneField(field)) {
      shape[key] = required
        ? z.string().trim().min(1, requiredMsg).refine(isValidPhone, phoneMsg)
        : z
            .string()
            .trim()
            .refine((v) => v === '' || isValidPhone(v), phoneMsg)
      continue
    }

    if (isNameField(field)) {
      shape[key] = required
        ? z.string().trim().min(1, requiredMsg).refine(isValidName, nameMsg)
        : z
            .string()
            .trim()
            .refine((v) => v === '' || isValidName(v), nameMsg)
      continue
    }

    if (isNumberField(field)) {
      shape[key] = required
        ? z.string().trim().min(1, requiredMsg).regex(NUMBER_INPUT_RE, numberMsg)
        : z.string().trim().regex(OPTIONAL_NUMBER_RE, numberMsg)
      continue
    }

    shape[key] = required
      ? z.string().trim().min(1, requiredMsg)
      : z.string().trim()
  }

  return z.object(shape)
}

export function buildContactDefaultValues(
  fields: ContactFormField[],
): Record<string, string | boolean> {
  const vals: Record<string, string | boolean> = {}
  for (const field of fields) {
    vals[fieldKey(field)] = isBooleanLikeField(field) ? false : ''
  }
  return vals
}

export function getFormService(): string {
  const service = process.env.NEXT_PUBLIC_FORM_SERVICE?.trim().replace(/^["']|["']$/g, '')
  if (!service) {
    throw new Error(
      'NEXT_PUBLIC_FORM_SERVICE is not set. Add formService to sites/<site-id>.json (or .env).',
    )
  }
  return service
}
